import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, TextInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Building, Plus, MapPin, Trash2 } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import TID from "@/constants/testIds";
import { ImportExportBar } from "@/components/foundation/ImportExportBar";

// Test data pending the final BU/plant structure from the customer's team (requirement #4).
const BUSINESS_UNITS = ["BU-1 · Automotive Components", "BU-2 · Industrial & Non-Auto"];

export default function Plants() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ code: "", name: "", city: "", state: "", business_unit: "", is_active: true });
    const [submitting, setSubmitting] = useState(false);
    const canEdit = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        api.get("/plants")
            .then((r) => setRows(r.data))
            .catch((e) => setError(formatApiError(e)));

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ code: "", name: "", city: "", state: "", business_unit: "", is_active: true });
        setModalOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setForm({ code: row.code, name: row.name, city: row.city, state: row.state, business_unit: row.business_unit || "", is_active: row.is_active });
        setModalOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editing) await api.put(`/plants/${editing.id}`, form);
            else await api.post("/plants", form);
            toast.success(editing ? "Plant updated" : "Plant created");
            setModalOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete plant ${row.code}? This action cannot be undone.`)) return;
        try {
            await api.delete(`/plants/${row.id}`);
            toast.success("Plant deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid={TID.plantsPage}>
            <PageHeader
                eyebrow="FOUNDATION · LAYER 2"
                title="Plants"
                subtitle="12 manufacturing locations powering ABC COMPANY's pricing. Each plant has its own machine fleet and rate baseline."
                actions={
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <ImportExportBar entity="plants" label="Plants" canImport={canEdit} onImported={load} />
                        {canEdit && (
                            <CopperButton onClick={openCreate} data-testid={TID.plantsAddBtn}>
                                <Plus className="h-4 w-4" /> Add Plant
                            </CopperButton>
                        )}
                    </div>
                }
            />

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}

            {rows && rows.length === 0 && (
                <EmptyState icon={Building} title="No plants yet" description="Add ABC COMPANY's first manufacturing location to get started." />
            )}

            {rows && rows.length > 0 && (
                <Card>
                    <table data-testid={TID.plantsTable} className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-2 px-4">Code</th>
                                <th className="eyebrow text-left py-2 px-4">Name</th>
                                <th className="eyebrow text-left py-2 px-4">Location</th>
                                <th className="eyebrow text-left py-2 px-4">State</th>
                                <th className="eyebrow text-left py-2 px-4">Business Unit</th>
                                <th className="eyebrow text-left py-2 px-4">Status</th>
                                <th className="eyebrow text-left py-2 px-4">Created</th>
                                <th className="eyebrow text-right py-2 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={TID.plantRow(r.id)} className="border-b border-slate-100">
                                    <td className="py-1.5 px-4 text-[13px] font-mono font-semibold text-slate-900">{r.code}</td>
                                    <td className="py-1.5 px-4 text-[13px]">{r.name}</td>
                                    <td className="py-1.5 px-4 text-[13px] text-slate-600">
                                        <span className="inline-flex items-center gap-1.5">
                                            <MapPin className="h-3 w-3 text-slate-400" /> {r.city}
                                        </span>
                                    </td>
                                    <td className="py-1.5 px-4 text-[13px] text-slate-600">{r.state}</td>
                                    <td className="py-1.5 px-4 text-[12.5px] text-slate-600">{r.business_unit || <span className="text-slate-400">Unassigned</span>}</td>
                                    <td className="py-1.5 px-4">
                                        <Pill tone={r.is_active ? "emerald" : "slate"}>{r.is_active ? "Active" : "Inactive"}</Pill>
                                    </td>
                                    <td className="py-1.5 px-4 text-[12px] font-mono tabular-nums text-slate-500">{fmtDate(r.created_at)}</td>
                                    <td className="py-1.5 px-4 text-right">
                                        {canEdit && (
                                            <div className="inline-flex gap-1">
                                                <SlateButton onClick={() => openEdit(r)} className="h-7 px-2.5 text-xs">Edit</SlateButton>
                                                <SlateButton
                                                    onClick={() => remove(r)}
                                                    variant="ghost"
                                                    className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                                    title={`Delete ${r.code}`}
                                                    data-testid={`plant-delete-${r.id}`}
                                                >
                                                    <Trash2 className="h-3 w-3" /> Delete
                                                </SlateButton>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? `Edit Plant — ${editing.code}` : "Add New Plant"}
                footer={
                    <>
                        <SlateButton variant="outline" onClick={() => setModalOpen(false)} data-testid={TID.modalCancel}>Cancel</SlateButton>
                        <CopperButton onClick={submit} disabled={submitting} data-testid={TID.modalSubmit}>
                            {submitting ? "Saving…" : (editing ? "Save Changes" : "Create Plant")}
                        </CopperButton>
                    </>
                }
            >
                <form onSubmit={submit} className="space-y-5">
                    <Field label="Plant Code" required helper="e.g., PUNE-05 (uppercase, hyphenated)">
                        <TextInput
                            value={form.code}
                            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                            placeholder="PUNE-05"
                            required
                            disabled={!!editing}
                        />
                    </Field>
                    <Field label="Plant Name" required>
                        <TextInput
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Pune Unit 5 — Bhosari"
                            required
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="City" required>
                            <TextInput value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Pune" required />
                        </Field>
                        <Field label="State" required>
                            <TextInput value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Maharashtra" required />
                        </Field>
                    </div>
                    <Field label="Business Unit" helper="Test data — pending the final BU/plant structure">
                        <Select value={form.business_unit} onChange={(e) => setForm({ ...form, business_unit: e.target.value })}>
                            <option value="">Unassigned</option>
                            {BUSINESS_UNITS.map((bu) => <option key={bu} value={bu}>{bu}</option>)}
                        </Select>
                    </Field>
                    <Field label="Status">
                        <Select value={form.is_active ? "active" : "inactive"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "active" })}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </Select>
                    </Field>
                </form>
            </Modal>
        </div>
    );
}